import { motion, Variants } from 'framer-motion';
import { FC, ReactNode, useEffect, useId } from 'react';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { Button } from '@components/atoms/Button';
import { useModalManager } from '@utils/modalManager';
import { Z_INDEX } from '@constants/zIndex';

const VARIANTS_OPACITY = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const SLIDER_VARIANTS: Variants = {
  hidden: { x: '100vw', transition: { duration: 0.3, ease: 'easeInOut' } },
  visible: { x: 0, transition: { duration: 0.3, ease: 'easeInOut' } },
  exit: { x: '100vw', transition: { duration: 0.3, ease: 'easeInOut' } },
};

interface ActionButton {
  text: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
  /**
   * Story-066: `SliderModal` renders no `role="dialog"` at all (unlike `Modal`, which this
   * story gives a `contentLabel`), so `data-testid` is the only stable selector its two
   * callers (`CreateEditTaskModal`, `TaskDetailModal`) have. Optional and additive — omitted
   * everywhere the story's file list doesn't need it, with no attribute rendered at all.
   */
  testId?: string;
}

interface Props {
  title?: ReactNode;
  isOpen: boolean;
  className?: string;
  disabled?: boolean;
  cancelText?: string;
  children?: ReactNode;
  confirmText?: string;
  closeSlider?: () => void;
  handleSubmit?: () => void;
  actionButtons?: ActionButton[];
  /**
   * Use secondary (higher) z-index layer for nested modals.
   * Set to true when this modal should appear on top of another modal.
   */
  isSecondaryLayer?: boolean;
}

export const SliderModal: FC<Props> = ({
  title,
  isOpen,
  className,
  cancelText,
  confirmText,
  closeSlider,
  handleSubmit,
  children = null,
  disabled = false,
  actionButtons,
  isSecondaryLayer = false,
}) => {
  // Use secondary z-index for nested modals (e.g., subtask modal on top of task modal)
  const backdropZIndex = isSecondaryLayer
    ? Z_INDEX.SLIDE_OVER_SECONDARY_BACKDROP
    : Z_INDEX.SLIDE_OVER_BACKDROP;
  const contentZIndex = isSecondaryLayer
    ? Z_INDEX.SLIDE_OVER_SECONDARY_CONTENT
    : Z_INDEX.SLIDE_OVER_CONTENT;
  const modalId = useId();
  const modalManager = useModalManager();

  useEffect(() => {
    if (isOpen) {
      modalManager.openModal(modalId, 'slideOver', true);
    } else {
      modalManager.closeModal(modalId);
    }

    return () => {
      modalManager.closeModal(modalId);
    };
  }, [isOpen, modalId, modalManager]);

  return (
    <>
      {isOpen && (
        <motion.div
          initial="hidden"
          animate={isOpen ? "visible" : "hidden"}
          exit="exit"
          variants={VARIANTS_OPACITY}
          className="fixed inset-0"
          style={{ zIndex: backdropZIndex }}>
          <div
            onClick={closeSlider}
            className="absolute bg-black opacity-40 top-0 left-0 w-full h-full"
          />
          <motion.div
            initial="hidden"
            animate={isOpen ? "visible" : "hidden"}
            exit="exit"
            className={`absolute overflow-auto overflow-x-hidden right-0 w-full sm:w-[75%] p-6
              bg-white dark:bg-gray-800 h-full top-0 transform shadow-xl ${className}`}
            style={{ zIndex: contentZIndex }}
            variants={SLIDER_VARIANTS}>
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                {title}
              </h1>
              <div
                className="p-1 text-gray-400 rounded-full cursor-pointer"
                onClick={closeSlider}>
                <ClearOutlinedIcon className="w-5 h-5" />
              </div>
            </div>
            <div className="mt-5">{children}</div>
            <div className="fixed bottom-6 left-6 right-6 flex gap-4 justify-end">
              {closeSlider && (
                <Button variant="secondary" onClick={closeSlider}>
                  {cancelText || 'Cancel'}
                </Button>
              )}
              {actionButtons && actionButtons.length > 0 ? (
                // Render custom action buttons
                actionButtons.map((button, index) => (
                  <Button
                    key={index}
                    variant={button.variant || 'primary'}
                    disabled={button.disabled || false}
                    onClick={button.onClick}
                    data-testid={button.testId}>
                    {button.text}
                  </Button>
                ))
              ) : (
                // Fallback to legacy single button
                handleSubmit && (
                  <Button
                    variant="primary"
                    disabled={disabled}
                    onClick={() => {
                      handleSubmit && handleSubmit();
                    }}>
                    {confirmText || 'Save'}
                  </Button>
                )
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};
